import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo } from 'react'

import TanstackTable from 'components/reusable/table/TanstackTable'
import { type ChartConfig } from 'components/ui/chart'

import NeedsAttentionPieChart from './NeedsAttentionPieChart'
import { type NAVStatisticsProps, type StatisticsType } from './types'
import { colors } from '../../../../../config/tailwind/colors'

const chartConfig: ChartConfig = {
  enrollment_type: {
    label: 'Enrollment Type',
  },
  items: {
    label: 'NAV Items',
  },
  medical: {
    label: 'Medical',
    color: colors.primitive.thatch[800],
  },
  dental: {
    label: 'Dental',
    color: colors.primitive.thatch[700],
  },
  vision: {
    label: 'Vision',
    color: colors.primitive.thatch[600],
  },
}

const getSortingValue = (stat: StatisticsType) => {
  switch (stat.enrollment_type) {
    case 'medical':
      return 1
    case 'dental':
      return 2
    case 'vision':
      return 3
    default:
      return 4
  }
}

function getChartDataFromStatistics (
  byEnrollmentTypeStats: Record<string, number> | undefined
): StatisticsType[] {
  return Object.entries(byEnrollmentTypeStats || {}).map(([key, value]) => ({
    enrollment_type: key,
    items: value,
  })).sort((a, b) => getSortingValue(a) - getSortingValue(b))
}

export const NAVEnrollmentTypePieChart = ({
  stats: byEnrollmentTypeStats,
}: NAVStatisticsProps) => {
  const chartData: StatisticsType[] = useMemo(() => {
    return getChartDataFromStatistics(byEnrollmentTypeStats)
  }, [byEnrollmentTypeStats])

  return (
    <NeedsAttentionPieChart
      title='Enrollment Type'
      nameKey='enrollment_type'
      dataKey='items'
      chartData={chartData}
      chartConfig={chartConfig}
    />
  )
}

const columnHelper = createColumnHelper<StatisticsType>()

const columns = [
  columnHelper.accessor('enrollment_type', {
    header: 'Type',
    cell: (info) => chartConfig[info.getValue() as string].label,
  }),
  columnHelper.accessor('items', {
    header: () => <div className='text-end'>Count</div>,
    cell: (info) => <div className='text-end'>{info.getValue()}</div>,
  }),
]

export const NAVEnrollmentTypeTable = ({
  stats: byEnrollmentTypeStats,
}: NAVStatisticsProps) => {
  const chartData: StatisticsType[] = useMemo(() => {
    return getChartDataFromStatistics(byEnrollmentTypeStats)
  }, [byEnrollmentTypeStats])

  const table = useReactTable({
    data: chartData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex: 0, pageSize: 3 } },
  })

  return (
    <div>
      <div className='flex items-center justify-center mb-4'>
        <div className='font-semibold'>Enrollment Type</div>
      </div>
      <TanstackTable
        table={table}
        paginationLabelSingular='item'
        showPageSelectionIfSinglePage={false}
      />
    </div>
  )
}
